import React, { useEffect, useState } from 'react';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { Container, Title } from './style'; // Assegure-se de que o arquivo de estilo está correto
import 'pdfjs-dist/build/pdf.worker.entry';
import { useParams } from 'react-router-dom'; 
import { apiOperation, headers } from '../../services/api';
import { Header } from '../Header';
import { Loading } from '../Loading';
import { Documento } from '../../models/analise';

interface Indicador {
    tipoParametro: number;
    descricaoParametro: string;
    secao: string;
    valoresPorAno: Record<string, number>;
    itensSelecionados: string;
}

interface Analise {
    analiseId: string;
    urlDocumentos: string[];
    indicadores: Indicador[];
}

const PdfButton = ({ url, label, onClick }: { url: string; label: string; onClick: (url: string) => void }) => (
    <button onClick={() => onClick(url)}
    style={{
        backgroundColor: 'var(--action-accent-surface)',
        fontSize: '20px',
        borderRadius: '4px',
        padding: '10px 20px',
        cursor: 'pointer',
        margin: '1%',
    }}>
        {label}
    </button>
);

const VisualizationPage = ({ pdfUrl }: { pdfUrl: string }) => {
    const defaultLayoutPluginInstance = defaultLayoutPlugin();
    if (!pdfUrl) {
        return <p>URL do PDF não fornecida.</p>;
    }
    return (
        <div style={{ height: "90vh", overflow: "auto" }}>
            <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js">
                <Viewer fileUrl={pdfUrl} plugins={[defaultLayoutPluginInstance]} />
            </Worker>
        </div>
    );
};

export const AppVisualization = () => {
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const { analiseId } = useParams<{ analiseId: string }>();
    const [analise, setAnalise] = useState<Analise | null>(null);
    const [loadingModal, setLoadingModal] = useState<boolean>(false);
    const [messagesLoading, setMessagesLoading] = useState<string>('');
    const [errorMsg, setErrorMsg] = useState<string>('');

    useEffect(() => {
        if (!analiseId) {
            console.warn('Analise ID não encontrado.');
            return;
        }
    
        async function fetchAnalysisData() {
            try {
                const config = {
                    headers: headers(),
                };
                const { data } = await apiOperation.get<Analise>(
                    `esteira/obter?analiseId=${analiseId}`,
                    config
                );
                setAnalise(data);
                const documentUrl = data.urlDocumentos[0];
                if (documentUrl) {
                    setPdfUrl(documentUrl);
                } else {
                    console.warn('URL do PDF não encontrada');
                }
            } catch (error) {
                console.error('Erro ao buscar dados da análise:', error);
            }
        }
    
        fetchAnalysisData();
    }, [analiseId]);


    const handleSalvarIndicadores = async (confirmar: boolean) => {
        if (!analise) return;

        setLoadingModal(true);
        setMessagesLoading("Atualizando informações...")

        const config = {
            headers: headers()
        }
        try {
            const response = await apiOperation.post(
                `/esteira/atualizar?confirmar=${confirmar}`,
                analise,
                config 
            );
            setLoadingModal(false);
        } catch (err) {
            const msgError = `${err}
            Atualize a pagina e tente novamente!`;
            setErrorMsg(msgError);
            setLoadingModal(false);
        }
    };

    const Download = async (params = "") => {

        setLoadingModal(true);
        setMessagesLoading("Atualizando informações...")

        try {
          const config = {
            headers: headers(),
          };
    
          let urlString = `analise/relatorio?analiseId=${params}`;
    
          const { data } = await apiOperation.get<Documento>(urlString, config);
    
          window.open(data.valor, "_blank", "noopener,noreferrer");

          setLoadingModal(false);
    
        } catch (error: any) {
            const msgError = `${error}
            Atualize a pagina e tente novamente!`;
            setErrorMsg(msgError);
            setLoadingModal(false);
        }
      };

    const handleValueChange = (ano: string, tipoParametro: number, novoValor: number) => {
        setAnalise((prevAnalise) => {

            if (!prevAnalise) return prevAnalise;

            const indicadoresAtualizados = prevAnalise.indicadores.map((indicador) => {
                if (indicador.tipoParametro === tipoParametro) {
                    return {
                        ...indicador,
                        valoresPorAno: {
                            ...indicador.valoresPorAno,
                            [ano]: novoValor,
                        },
                    };
                }
                return indicador;
            });

            return { ...prevAnalise, indicadores: indicadoresAtualizados };
        });
    };

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>, tipoParametro: number) => {
        setAnalise((prev) => {
            if (!prev) return prev;
    
            return {
                ...prev,
                indicadores: prev.indicadores.map((indicador) =>
                    indicador.tipoParametro === tipoParametro
                        ? { ...indicador, itensSelecionados: event.target.value }
                        : indicador
                ),
            };
        });
    };

    const handleInputChange = (
        ano: string,
        tipoParametro: number,
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const novoValor = parseFloat(event.target.value);
        if (!isNaN(novoValor)) {
            handleValueChange(ano, tipoParametro, novoValor);
        }
    };
    const anos = Array.from(
        new Set(analise?.indicadores.flatMap((indicador) => Object.keys(indicador.valoresPorAno)))
    );

    return (
        <Container>
            <Header />
            <Loading
                mode='blurry'
                hidden={!loadingModal}
                messages={messagesLoading}
            />
            <div className="Container1">
                <Title style={{ textAlign: 'center', margin: 'revert' }}>Esteira de documentos</Title>
                
                <div>
                    {analise?.urlDocumentos.map((url, index) => (
                        <PdfButton
                            key={index}
                            url={url}
                            label={`Abrir PDF ${index + 1}`}
                            onClick={setPdfUrl}
                        />
                    ))}
                    <br/>
                    <button
                        onClick={() => handleSalvarIndicadores(false)}
                        style={{
                            backgroundColor: 'var(--success)',
                            color: 'var(--text-on-inverse)',
                            fontSize: '16px',
                            borderRadius: '4px',
                            padding: '10px 20px',
                            cursor: 'pointer',
                            margin: '1%',
                        }}
                    >
                        Atualizar dados
                    </button>

                    <button
                        onClick={() => handleSalvarIndicadores(true)}
                        style={{
                            backgroundColor: 'var(--success)',
                            color: 'var(--text-on-inverse)',
                            fontSize: '16px',
                            borderRadius: '4px',
                            padding: '10px 20px',
                            cursor: 'pointer',
                            margin: '1%',
                        }}
                    >
                        Confirmar Atualização
                    </button>
                    
                    <button
                        onClick={() => Download(analise?.analiseId)}
                        style={{
                            backgroundColor: 'var(--success)',
                            color: 'var(--text-on-inverse)',
                            fontSize: '16px',
                            borderRadius: '4px',
                            padding: '10px 20px',
                            cursor: 'pointer',
                            margin: '1%',
                        }}
                    >
                        Baixar relatório
                    </button>

                </div>
            </div>
            <div className="row">
                <div className="col1">
                    {pdfUrl && <VisualizationPage pdfUrl={pdfUrl} />}
                </div>
                <div className="col2">
                    <div style={{ height: "90vh", overflowY: "auto", display: 'block'}}>
                        <table style={{
                            width: '100%',
                            border: '1px solid black',
                            borderCollapse: 'collapse',
                            tableLayout: 'fixed'
                        }}>
                            <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--surface-raised)', zIndex: 10 }}>
                                <tr>
                                    <th style={{ border: '1px solid black', padding: '8px', width: '150px' }}>Parâmetro</th>
                                    {anos.map((ano) => (
                                        <th key={ano} style={{ border: '1px solid black', padding: '8px', width: '80px' }}>{ano}</th>
                                    ))}
                                    <th style={{ border: '1px solid black', padding: '8px', width: '150px' }}>Itens Classificados</th>
                                </tr>
                            </thead>
                            <tbody>
                                {analise?.indicadores.map((indicador) => (
                                    <tr key={indicador.tipoParametro} >
                                        <td style={{ border: '1px solid black', padding: '8px', width: '150px' }}>{indicador.descricaoParametro}</td>
                                        {anos.map((ano) => (
                                            <td key={ano} style={{ border: '1px solid black', padding: '8px' }}>
                                                <input
                                                    type="number"
                                                    defaultValue={indicador.valoresPorAno[ano] || ''}
                                                    onBlur={(e) => handleInputChange(ano, indicador.tipoParametro, e)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '5px',
                                                        border: '1px solid var(--border-default)',
                                                        boxSizing: 'border-box',
                                                    }}
                                                />
                                            </td>
                                        ))}
                                        <td style={{ border: '1px solid black', padding: '8px', width: '150px' }}>
                                            <textarea
                                                value={indicador.itensSelecionados}
                                                onChange={(event) => handleChange(event, indicador.tipoParametro)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div className="Container1">
                <br />
                <br />
                <br />
                <br />
                <br />
            </div>
        </Container>
    );
};
